// Reading a replay without freezing the page. A 15-minute match is tens of megabytes of JSON; one
// JSON.parse of it blocks the main thread for the better part of a second. The file is one object whose
// `frames` array comes first (format.ts: `{"format":2,"frames":[...],"header":{...}}`), so this finds the
// frame boundaries with a small scanner, parses and validates the frames in batches, and yields to the
// browser between slices of work. Anything not shaped like that falls back to one plain parse.
import { parseReplayFile, REPLAY_FORMAT, type ReplayFile, type ReplayFrame } from '../../shared/replay/format.js';

const PREFIX = `{"format":${REPLAY_FORMAT},"frames":[`;
const BATCH_FRAMES = 40;
const DECODE_SLICE = 1 << 20;

/** Gives the browser a turn. Not setTimeout: a background tab clamps timers to once a second, which would
    stretch a load that yields dozens of times into minutes. */
export function nextTask(): Promise<void> {
  if (typeof MessageChannel === 'undefined') return new Promise(r => setTimeout(r, 0));
  return new Promise(r => { const ch = new MessageChannel(); ch.port1.onmessage = () => { ch.port1.close(); r(); }; ch.port2.postMessage(0); });
}

/** The body as text, decoded as it arrives rather than in one go at the end. */
export async function readText(res: Response): Promise<string> {
  if (!res.body) return res.text();
  const reader = res.body.getReader(), decoder = new TextDecoder(), parts: string[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    // A cached body can arrive as one huge chunk: decode it a slice at a time, with a turn in between.
    for (let off = 0; off < value.length; off += DECODE_SLICE) {
      parts.push(decoder.decode(value.subarray(off, off + DECODE_SLICE), { stream: true }));
      if (value.length > DECODE_SLICE) await nextTask();
    }
  }
  parts.push(decoder.decode());
  return parts.join('');
}

/** Same answer as `parseReplayFile(JSON.parse(text))` (null for anything unusable), in slices of at most
    about `budgetMs` of work between `pause()`s. */
export async function parseReplayText(text: string, pause: () => Promise<void> = nextTask, budgetMs = 12): Promise<ReplayFile | null> {
  const whole = () => { try { return parseReplayFile(JSON.parse(text)); } catch { return null; } };
  if (!text.startsWith(PREFIX)) return whole();

  // Top-level frame boundaries: [start, end) of each frame's own text.
  const bounds: number[] = [];
  let depth = 0, inStr = false, esc = false, start = PREFIX.length, end = -1, t = performance.now();
  for (let i = start; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (inStr) {
      if (esc) esc = false;
      else if (c === 92) esc = true;        // backslash
      else if (c === 34) inStr = false;     // closing quote
      continue;
    }
    if (c === 34) inStr = true;
    else if (c === 123 || c === 91) depth++;   // { [
    else if (c === 125 || c === 93) {         // } ]
      if (depth === 0) { if (i > start) bounds.push(start, i); end = i; break; }
      depth--;
    } else if (c === 44 && depth === 0) { bounds.push(start, i); start = i + 1; }  // ,
    if ((i & 0xffff) === 0 && performance.now() - t > budgetMs) { await pause(); t = performance.now(); }
  }
  if (end < 0 || text[end] !== ']' || text[end + 1] !== ',') return whole();

  let rest: { header?: unknown };
  try { rest = JSON.parse('{' + text.slice(end + 2)) as { header?: unknown }; } catch { return whole(); }

  const frames: ReplayFrame[] = [];
  let header: ReplayFile['header'] | null = null;
  t = performance.now();
  for (let b = 0; b < bounds.length; b += 2 * BATCH_FRAMES) {
    const last = Math.min(bounds.length, b + 2 * BATCH_FRAMES) - 1;
    let batch: unknown;
    try { batch = JSON.parse('[' + text.slice(bounds[b]!, bounds[last]!) + ']'); } catch { return null; }
    // The same tolerant check as a whole file: bad frames dropped, order enforced (across batches too).
    const part = parseReplayFile({ format: REPLAY_FORMAT, frames: batch, header: rest.header });
    if (part) {
      header = part.header;
      for (const f of part.frames) if (!frames.length || f.world.tick > frames[frames.length - 1]!.world.tick) frames.push(f);
    }
    if (performance.now() - t > budgetMs) { await pause(); t = performance.now(); }
  }
  return header && frames.length ? { format: REPLAY_FORMAT, frames, header } : null;
}
